import { StyleSheet, Text, View, TouchableOpacity } from 'react-native';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { STYLE_VARS } from '../../../constants/style-vars';
import { Image } from '../../../components/Image';
import dayjs from 'dayjs';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { Button } from '../../../components/Button';
import { FONTS } from '../../../constants';
import { LineItem, Order } from '../../../types/woocommerce.types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../services/redux';
import { IProduct } from '../../../models/IProduct';
import { updateCart } from '../../../services/redux/reducers/CartSlice';
import { Alert } from '../../../services/alert';
import { wooCommerceApi } from '../../../services/api/wooCommerceApi';
import { Loading } from '../../../components';

interface OrderCollapseProps {
  orderDetails: Order;
  onCancel: () => void;
}

const OrderCollapse: FC<OrderCollapseProps> = ({ orderDetails, onCancel }) => {
  const [open, setOpen] = useState<boolean>(false);
  const isDesktop = useIsDesktop();
  const { items } = useSelector((redux: RootState) => redux.cartReducer);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleReorder = useCallback(() => {
    let newCartData: IProduct[] = items;

    orderDetails.line_items.map((product) => {
      if (items.filter((e) => e.id === product.product_id).length > 0) {
        newCartData = newCartData.map((data) => {
          if (data.id === product.product_id) {
            return {
              ...data,
              amount: data.amount + product.quantity,
            };
          }
          return data;
        });
      } else {
        newCartData = [
          ...newCartData,
          {
            id: product.product_id,
            photo: product.image.src ?? '',
            totalAmount: product.quantity,
            amount: product.quantity,
            price: product.price,
            type: '',
            productName: product.name,
          },
        ];
      }
    });

    dispatch(updateCart(newCartData));

    Alert.alert(
      '',
      'All products from the order have been added to your cart',
      [
        {
          text: 'OK',
          onPress: () => {},
        },
      ]
    );
  }, [items, orderDetails, dispatch]);

  const handleCancelOrder = useCallback(() => {
    setIsLoading(true);
    wooCommerceApi
      .put(`orders/${orderDetails.id}`, {
        status: 'cancelled',
      })
      .then((response: any) => {
        Alert.alert('Cancel Successful', 'Your order has been cancel', [
          {
            text: 'OK',
            onPress: () => {},
          },
        ]);
      })
      .catch((error: any) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
        onCancel();
      });
  }, [onCancel, orderDetails]);

  const renderHeaderCollapse = useMemo(() => {
    if (isDesktop()) {
      return (
        <TouchableOpacity
          style={styles.header}
          onPress={() => {
            setOpen(!open);
          }}
        >
          <View style={{ flexDirection: 'row', flex: 1 }}>
            <View
              style={{
                width: '20%',
                paddingVertical: 10,
                flexDirection: 'row',
                flex: 1,
                alignItems: 'center',
                paddingLeft: 20,
              }}
            >
              <Image
                source={
                  open == true
                    ? require('../../../assets/up-arrow.png')
                    : require('../../../assets/down-arrow.png')
                }
                style={{
                  width: 15,
                  height: 8,
                  marginRight: isDesktop() ? 10 : 5,
                }}
              />
              <Text style={styles.headerText}>
                {dayjs(orderDetails.date_created).format('DD MMMM YYYY')}
              </Text>
            </View>
            <View
              style={{
                width: '30%',
                paddingVertical: 10,
                paddingLeft: 10,
                backgroundColor: STYLE_VARS.fccLightGrey,
              }}
            >
              <Text style={styles.headerText}>{orderDetails.id}</Text>
            </View>
            <View
              style={{ width: '27%', paddingVertical: 10, paddingLeft: 10 }}
            >
              <Text style={styles.headerText}>HK${orderDetails.total}</Text>
            </View>
            <View
              style={{
                width: '23%',
                paddingVertical: 10,
                backgroundColor: STYLE_VARS.fccLightGrey,
                paddingLeft: 20,
              }}
            >
              <Text style={styles.headerText}>
                {/* {orderDetails.status == 1
                  ? 'Processing'
                  : orderDetails.status == 2
                  ? 'Complete'
                  : 'Cancelled'} */}
                {orderDetails.status}
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity
        style={[
          styles.header,
          isDesktop() && {
            paddingHorizontal: 20,
            paddingVertical: 10,
          },
        ]}
        onPress={() => {
          setOpen(!open);
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            paddingVertical: 10,
            flex: 1,
            justifyContent: 'space-between',
          }}
        >
          <View
            style={{
              width: '25%',
              flexDirection: 'row',
              alignItems: 'center',
              paddingRight: 10,
            }}
          >
            <Image
              source={
                open == true
                  ? require('../../../assets/up-arrow.png')
                  : require('../../../assets/down-arrow.png')
              }
              style={{
                width: 15,
                height: 8,
                marginRight: isDesktop() ? 10 : 5,
              }}
            />
            <Text style={styles.headerText}>
              {dayjs(orderDetails.date_created).format('DD MMM YYYY')}
            </Text>
          </View>
          <Text
            style={[
              styles.headerText,
              {
                alignItems: 'flex-start',
              },
            ]}
          >
            {orderDetails.id}
          </Text>
          <Text
            style={[
              styles.headerText,
              {
                alignItems: 'flex-start',
              },
            ]}
          >
            HK${Math.trunc(orderDetails.total)}
          </Text>
          <Text
            style={[
              styles.headerText,
              {
                width: '30%',
                textAlign: 'right',
              },
            ]}
          >
            {orderDetails.status}
          </Text>
        </View>
      </TouchableOpacity>
    );
  }, [orderDetails, open, isDesktop]);

  const renderCollapseBody = useMemo(() => {
    if (open === true) {
      return (
        <View style={{ marginTop: 10 }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: 10,
              paddingHorizontal: isDesktop() ? 20 : 0,
            }}
          >
            <Text
              style={[
                styles.bodyText,
                {
                  width: '40%',
                },
              ]}
            >
              Product
            </Text>
            <Text
              style={[
                styles.bodyText,
                {
                  width: '25%',
                },
              ]}
            >
              Quantity
            </Text>
            <Text
              style={[
                styles.bodyText,
                {
                  width: '25%',
                  textAlign: 'right',
                },
              ]}
            >
              Price
            </Text>
          </View>
          {orderDetails.line_items?.map((product) => (
            <View
              key={product.id}
              style={[styles.product, isDesktop() && { paddingHorizontal: 20 }]}
            >
              <View
                style={{
                  flexDirection: 'column',
                  width: '40%',
                }}
              >
                {product.image && product.image.src && (
                  <Image
                    source={{ uri: product.image.src }}
                    alt="Product photo"
                    style={{
                      width: 70,
                      height: 70,
                      resizeMode: 'contain',
                      borderColor: STYLE_VARS.fccDarkGrey,
                      borderWidth: 0.5,
                      marginRight: 10,
                      marginBottom: 10,
                    }}
                  />
                )}
                <Text style={[styles.bodyText, { flex: 1 }]}>
                  {product.name}
                </Text>
              </View>
              <Text
                style={[
                  styles.productTextNormal,
                  {
                    width: '25%',
                  },
                ]}
              >
                {product.quantity}
              </Text>
              <Text
                style={[
                  styles.productTextNormal,
                  {
                    width: '25%',
                    textAlign: 'right',
                  },
                ]}
              >
                HK${product.price}
              </Text>
            </View>
          ))}
          <View
            style={[
              {
                flexDirection: 'row',
                justifyContent: 'center',
                columnGap: 20,
                marginBottom: 20,
              },
            ]}
          >
            {orderDetails.status === 'pending' && (
              <Button
                style={STYLE_VARS.dangerButton}
                text="CANCEL"
                className=""
                onPress={() => {
                  Alert.alert('Cancel', 'You want to cancel?', [
                    {
                      text: 'Ok',
                      onPress: () => {
                        handleCancelOrder();
                      },
                    },
                    {
                      text: 'Cancel',
                      onPress: () => {},
                    },
                  ]);
                }}
              />
            )}
            <Button
              style={STYLE_VARS.blueButton}
              text="REORDER"
              onPress={() => {
                handleReorder();
              }}
            />
          </View>
        </View>
      );
    } else {
      return <></>;
    }
  }, [open, orderDetails, isDesktop, handleReorder, handleCancelOrder]);

  return (
    <>
      <View
        style={
          isDesktop()
            ? {
                borderColor: STYLE_VARS.fccDarkGrey,
                borderWidth: 0.5,
                marginBottom: 10,
              }
            : {
                paddingHorizontal: 20,
                marginBottom: 10,
                borderTopWidth: 0.5,
                borderBottomWidth: 0.5,
                borderBottomColor: STYLE_VARS.fccDarkGrey,
                borderTopColor: STYLE_VARS.fccDarkGrey,
              }
        }
      >
        {renderHeaderCollapse}
        {renderCollapseBody}
      </View>
      {isLoading && <Loading isFullScreen={true} />}
    </>
  );
};

export default OrderCollapse;

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerText: {
    fontSize: 13,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: STYLE_VARS.fccDarkerGrey,
    fontWeight: '500',
    fontFamily: FONTS.IowanOldStyleBTRoman,
  },
  bodyText: {
    fontSize: 13,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: STYLE_VARS.textColor,
    fontWeight: '500',
    fontFamily: FONTS.IowanOldStyleBTRoman,
  },
  product: {
    paddingVertical: 15,
    borderTopColor: STYLE_VARS.fccDarkerGrey,
    borderTopWidth: 0.5,
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  productTextNormal: {
    fontSize: 14,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: STYLE_VARS.fccDarkerGrey,
    fontWeight: '500',
    fontFamily: FONTS.IowanOldStyleBTRoman,
  },
});
